import * as React from 'react';
import {createView, createViewConfig, keyframes} from '@sail/engine';
import type {IconAsset} from '@sail/icons/types';
import {
  accept,
  tasks,
  cancel,
  checkCircleFilled,
  negativeCircleFilled,
} from '@sail/icons/react/Icon';
import type {View} from '@sail/engine';
import {tokens} from '@sail/tokens';
import {css} from '../css';
import {view} from '../view';
import {Icon} from './Icon';
import type {IconProps} from './Icon';

export type ProgressIndicatorStatus =
  | 'not-started'
  | 'in-progress'
  | 'blocked'
  | 'complete'
  | number;

export type ProgressIndicatorSize = 'small' | 'medium' | 'large';

type VariantDefaults = {
  iconPadding: number;
  donutThickness: number;
  size: number;
};

const LARGE_SIZE = 42;
const MEDIUM_SIZE = 20;
const SMALL_SIZE = 12;
const LARGE_ICON_PADDING = 9;
const MEDIUM_ICON_PADDING = 1;
const SMALL_ICON_PADDING = 0;
const LARGE_DONUT_THICKNESS = 4;
const MEDIUM_DONUT_THICKNESS = 3;
const SMALL_DONUT_THICKNESS = 2;

const VARIANT_DEFAULTS: {
  [K in ProgressIndicatorSize]: VariantDefaults;
} = {
  small: {
    iconPadding: SMALL_ICON_PADDING,
    donutThickness: SMALL_DONUT_THICKNESS,
    size: SMALL_SIZE,
  },
  medium: {
    iconPadding: MEDIUM_ICON_PADDING,
    donutThickness: MEDIUM_DONUT_THICKNESS,
    size: MEDIUM_SIZE,
  },
  large: {
    iconPadding: LARGE_ICON_PADDING,
    donutThickness: LARGE_DONUT_THICKNESS,
    size: LARGE_SIZE,
  },
};

/**
 * Get icon size based on progress indicator size and status.
 *
 * @param size Progress indicator size.
 * @param status Progress indicator status.
 * @returns Icon size string.
 */
function getIconSize(
  size: ProgressIndicatorSize,
  status: ProgressIndicatorStatus,
): IconProps['size'] {
  switch (size) {
    case 'large':
      switch (status) {
        case 'complete':
        case 'blocked':
          return 'medium';
        default:
          return 'small';
      }
    case 'medium':
      switch (status) {
        case 'complete':
        case 'blocked':
          return 'medium';
        default:
          return 'small';
      }
    default:
      return 'xsmall';
  }
}

/**
 * Get default icon to render based on progress indicator size and status.
 *
 * @param size Progress indicator size.
 * @param status Progress indicator status.
 * @returns IconAsset to render.
 */
function getIcon(
  size: ProgressIndicatorSize,
  status: ProgressIndicatorStatus,
): IconAsset {
  if (typeof status === 'number') {
    if (status >= 100) {
      return accept;
    } else {
      return tasks;
    }
  }

  if (typeof status === 'string') {
    switch (status) {
      case 'complete':
        switch (size) {
          case 'small':
          case 'medium':
            return checkCircleFilled;
          default:
            return accept;
        }
      case 'blocked':
        switch (size) {
          case 'small':
          case 'medium':
            return negativeCircleFilled;
          default:
            return cancel;
        }
      case 'in-progress':
      case 'not-started':
      default:
        return tasks;
    }
  }
  return tasks;
}

/**
 * Get icon fill color based on progress indicator size and status.
 *
 * @param size Progress indicator size.
 * @param status Progress indicator status.
 * @returns Icon fill color token.
 */
function getIconFill(
  size: ProgressIndicatorSize,
  status: ProgressIndicatorStatus,
) {
  if (typeof status === 'number') {
    return css({fill: 'subdued'});
  }

  switch (status) {
    case 'complete':
      switch (size) {
        case 'large':
          return css({fill: 'feedback.success'});
        default:
          return css({fill: tokens['color.background.action.primary']});
      }
    case 'blocked':
      return css({fill: 'feedback.critical'});
    default:
      return css({fill: 'subdued'});
  }
}

/**
 * Get icon background color based on progress indicator size and status.
 *
 * @param size Progress indicator size.
 * @param status Progress indicator status.
 * @returns Icon background color token/string.
 */
function getIconBackgroundColor(
  size: ProgressIndicatorSize,
  status: ProgressIndicatorStatus,
) {
  switch (status) {
    case 'complete':
      switch (size) {
        case 'small':
        case 'medium':
          return css({backgroundColor: 'surface'});
        default:
          return css({backgroundColor: 'inherit'});
      }
    case 'blocked':
      switch (size) {
        case 'small':
        case 'medium':
          return css({backgroundColor: 'surface'});
        default:
          return css({backgroundColor: 'inherit'});
      }
    default:
      return css({backgroundColor: 'inherit'});
  }
}

/**
 * Get background circle color based on progress indicator status.
 *
 * @param status Progress indicator status.
 * @returns Background circle color token.
 */
function getBackgroundCircleColor(
  size: ProgressIndicatorSize,
  status: ProgressIndicatorStatus,
) {
  if (typeof status === 'number') {
    return css({backgroundColor: tokens['color.neutral.100']});
  }

  switch (status) {
    case 'complete':
      return css({backgroundColor: 'feedback.success'});
    case 'blocked':
      return css({backgroundColor: 'feedback.critical'});
    default:
      if (size === 'large') {
        return css({backgroundColor: tokens['color.neutral.50']});
      }
      return css({backgroundColor: tokens['color.neutral.100']});
  }
}

function getDonutSize(size: ProgressIndicatorSize) {
  switch (size) {
    case 'large':
      return css({height: LARGE_SIZE, width: LARGE_SIZE});
    case 'medium':
      return css({height: MEDIUM_SIZE, width: MEDIUM_SIZE});
    default:
      return css({height: SMALL_SIZE, width: SMALL_SIZE});
  }
}

/**
 * Get the top/left offset of the progress indicator icon based on progress indicator size/status.
 *
 * @param size Progress indicator size.
 * @param status Progress indicator status.
 * @returns The top/left progress indicator icon offset.
 */
function getIconOffset(
  size: ProgressIndicatorSize,
  status: ProgressIndicatorStatus,
) {
  switch (status) {
    case 'complete':
    case 'blocked':
      switch (size) {
        case 'large':
          return css({
            left: LARGE_ICON_PADDING + 2,
            top: LARGE_ICON_PADDING + 2,
          });
        case 'medium':
        default:
          return css({left: 'space.0', top: 'space.0'});
      }
    default:
      switch (size) {
        case 'large':
          return css({
            left: LARGE_ICON_PADDING + LARGE_DONUT_THICKNESS,
            top: LARGE_ICON_PADDING + LARGE_DONUT_THICKNESS,
          });
        case 'medium':
          return css({
            left: MEDIUM_ICON_PADDING + MEDIUM_DONUT_THICKNESS,
            top: MEDIUM_ICON_PADDING + MEDIUM_DONUT_THICKNESS,
          });
        default:
          return css({
            left: SMALL_ICON_PADDING + SMALL_DONUT_THICKNESS,
            top: SMALL_ICON_PADDING + SMALL_DONUT_THICKNESS,
          });
      }
  }
}

/**
 * Draw the medium/not-started progress indicator - dashed circle outline.
 *
 * @param size Progress indicator size.
 * @param status Progress indicator status.
 * @param foregroundColor Color of the dashed outline - color resolved from token + context.
 * @returns The medium/not-started progress indicator element, or null if !(size === 'medium && status === 'not-started').
 */
const MediumNotStarted = createView(
  ({
    size,
    status,
    foregroundColor,
  }: {
    size: ProgressIndicatorSize;
    status: ProgressIndicatorStatus;
    foregroundColor: string;
  }) => {
    if (size !== 'medium' || status !== 'not-started') {
      return null;
    }

    const diameter = VARIANT_DEFAULTS[size].size;
    const radius = diameter / 2;
    const thickness = 2;

    return (
      <view.svg
        height={diameter}
        width={diameter}
        css={{display: 'inline', position: 'absolute'}}
      >
        <view.circle
          stroke={foregroundColor}
          strokeDasharray="3px 4px"
          strokeLinecap="round"
          css={{
            transformOrigin: '50% 50%',
          }}
          strokeWidth={thickness}
          fill="transparent"
          r={radius - thickness / 2}
          cx={radius}
          cy={radius}
        />
      </view.svg>
    );
  },
);

/**
 * Draw the background circle, if required, based on progress indicator size/status.
 *
 * @param size Progress indicator size.
 * @param status Progress indicator status.
 * @returns The background circle element, or null if none is required.
 */

type BackgroundCircleProps = {
  size: ProgressIndicatorSize;
  status: ProgressIndicatorStatus;
};

const BackgroundCircle = createView<BackgroundCircleProps, 'div'>(
  ({size, status, inherits}) => {
    if (typeof status === 'number') {
      return null;
    }

    switch (size) {
      case 'medium':
        return null;
      case 'large':
        if (status === 'in-progress') {
          return null;
        }
        break;
      case 'small':
        if (status !== 'not-started') {
          return null;
        }
        break;
      default:
        break;
    }

    return (
      <view.div
        inherits={inherits}
        css={[getDonutSize(size), getBackgroundCircleColor(size, status)]}
      />
    );
  },
  {
    css: {
      top: 'space.0',
      left: 'space.0',
      position: 'absolute',
      display: 'inline',
      borderRadius: '50%',
    },
  },
);

type ProgressIndicatorDonutProps = {
  size: ProgressIndicatorSize;
  status: ProgressIndicatorStatus;
  prevStatus: ProgressIndicatorStatus;
  foregroundColor: string;
  backgroundColor: string;
  animate: boolean;
};

/**
 * Draw the percentage progress indicator donut if needed.
 *
 * @param size Progress indicator size.
 * @param status Progress indicator status.
 * @param foregroundColor Donut indicator foreground color.
 * @param backgroundColor Donut indicator background color.
 * @returns The percentage progress donut indicator element, or null if none should be rendered.
 */
const ProgressIndicatorDonut = createView<ProgressIndicatorDonutProps, 'svg'>(
  ({size, status, prevStatus, foregroundColor, backgroundColor, animate}) => {
    const animateRef = React.useRef<SVGAnimationElement>(null);

    React.useEffect(() => {
      /* Re-animate when status changes. */
      animateRef.current &&
        animateRef.current.beginElement &&
        animateRef.current.beginElement();
    }, [status, animateRef]);

    if (typeof status !== 'number' && status !== 'in-progress') {
      return <></>;
    }

    let prevStatusNorm = prevStatus;

    /* Set previous progress - for animating the progress change. */
    if (typeof prevStatus !== 'number' && prevStatus !== 'in-progress') {
      if (prevStatus === 'not-started') {
        prevStatusNorm = 0;
      } else if (prevStatus === 'complete') {
        prevStatusNorm = 100;
      }
    }

    /* If previous progress and current progress is the same, animate from zero until current progress. */
    if (prevStatusNorm === status) {
      prevStatusNorm = 0;
    }

    const radius = VARIANT_DEFAULTS[size].size / 2;
    const thickness = VARIANT_DEFAULTS[size].donutThickness;
    const circumference = Math.PI * 2 * (radius - thickness / 2);
    const progress = status === 'in-progress' ? 1 : Math.max(0.1, status / 100);
    const prevProgress =
      typeof prevStatusNorm !== 'number' ? 1 : prevStatusNorm / 100;
    const dashOffset = circumference - progress * circumference;
    const prevDashOffset = circumference - prevProgress * circumference;

    return (
      <view.svg
        height={radius * 2}
        width={radius * 2}
        css={{
          display: 'inline',
          position: 'absolute',
          transformOrigin: `center center`,
          transformBox: 'fill-box',
          transform: 'rotate(-90deg)',
        }}
        version="2.0"
      >
        {/* Background gray circle. */}
        <circle
          stroke={backgroundColor}
          strokeWidth={thickness}
          fill="transparent"
          r={radius - thickness / 2}
          cx={radius}
          cy={radius}
        />

        {/* Progress indicator circle. */}
        <circle
          stroke={foregroundColor}
          strokeDasharray={`${circumference} ${circumference}`}
          strokeLinecap="round"
          strokeWidth={thickness}
          fill="transparent"
          r={radius - thickness / 2}
          cx={radius}
          cy={radius}
        >
          <animate
            ref={animateRef}
            attributeName="stroke-dashoffset"
            dur={animate ? '1.5s' : '0.01s'}
            values={`${prevDashOffset};${dashOffset}`}
            fill="freeze"
            calcMode="linear"
          />
        </circle>
      </view.svg>
    );
  },
);

/**
 * Draw the completed animation.
 *
 * @param size Progress indicator size.
 * @param status Progress indicator status.
 * @param foregroundColor Donut indicator foreground color.
 * @param backgroundColor Donut indicator background color.
 * @returns The percentage progress donut indicator element, or null if none should be rendered.
 */
const CompletedAnimation = createView(
  ({
    size,
    foregroundColor,
    backgroundColor,
  }: {
    size: ProgressIndicatorSize;
    foregroundColor: string;
    backgroundColor: string;
  }) => {
    const radius = VARIANT_DEFAULTS[size].size / 2;
    const thickness = VARIANT_DEFAULTS[size].donutThickness;
    const circumference = Math.PI * 2 * (radius - thickness / 2);

    return (
      <view.svg
        height={radius * 2}
        width={radius * 2}
        css={{display: 'inline', position: 'absolute', overflow: 'visible'}}
      >
        {/* Background gray circle. */}
        <circle
          stroke={backgroundColor}
          strokeWidth={thickness}
          fill="transparent"
          opacity={0}
          r={radius - thickness / 2}
          cx={radius}
          cy={radius}
        >
          <animate
            attributeName="opacity"
            dur="2s"
            values="1;0"
            calcMode="linear"
          />
        </circle>

        {/* Progress indicator circle. */}

        <view.circle
          stroke={foregroundColor}
          strokeDasharray={`${circumference} ${circumference}`}
          css={{
            transformOrigin: '50% 50%',
            strokeDashoffset: circumference,
          }}
          strokeLinecap="round"
          strokeWidth={thickness}
          fill="transparent"
          r={radius - thickness / 2}
          cx={radius}
          cy={radius}
        >
          <animateTransform
            attributeName="transform"
            attributeType="XML"
            type="rotate"
            from="0"
            to="720"
            dur="1.5s"
          />
          <animate
            attributeName="stroke-dashoffset"
            dur="1.5s"
            values={`${circumference};${circumference / 3};${circumference}`}
            calcMode="linear"
          />
          <animate
            attributeName="opacity"
            dur="1.5s"
            values="0;1;0"
            calcMode="linear"
          />
        </view.circle>

        <view.circle
          stroke={foregroundColor}
          strokeDasharray="0"
          css={{
            transformOrigin: '50% 50%',
          }}
          strokeLinecap="round"
          strokeOpacity={0}
          strokeWidth={thickness / 1.5}
          fill="transparent"
          r={radius - thickness / 2}
          cx={radius}
          cy={radius}
        >
          <animate
            attributeName="stroke-dasharray"
            dur="0.5s"
            begin="1s"
            values={`0 ${circumference / 8 - thickness / 1.5}; 0 ${
              circumference / 4 - thickness / 1.5
            }`}
            calcMode="linear"
          />
          <animate
            attributeName="r"
            dur="0.5s"
            begin="1s"
            values={`${radius};${radius * 2}`}
            calcMode="linear"
          />
          <animate
            attributeName="stroke-opacity"
            dur="0.5s"
            begin="1s"
            values="1;0"
            calcMode="linear"
          />
        </view.circle>
      </view.svg>
    );
  },
);

/**
 * Should the icon be rendered? Based on progress indicator size/status.
 *
 * @param size Progress indicator size.
 * @param status Progress indicator status.
 * @returns true/false indicating whether the icon should be rendered.
 */
function isShowIcon(
  size: ProgressIndicatorSize,
  status: ProgressIndicatorStatus,
) {
  switch (size) {
    case 'small':
    case 'medium':
      if (typeof status === 'number') {
        return false;
      } else {
        return status === 'complete' || status === 'blocked';
      }
    case 'large':
    default:
      return true;
  }
}

const iconInAnimation = keyframes({
  '0%': {
    transform: 'scale(0.5)',
  },
  '80%': {
    transform: 'scale(1.1)',
  },
  '100%': {
    transform: 'scale(1.0)',
  },
});

type ProgressIndicatorIconProps = {
  size: ProgressIndicatorSize;
  status: ProgressIndicatorStatus;
  animate: boolean;
  icon?: IconAsset;
};

/**
 * Draw the progress indicator icon, if required.
 *
 * @param size Progress indicator size.
 * @param status Progress indicator status.
 * @param icon Icon override.
 * @returns The progress indicator icon, if one should be rendered.
 */
const ProgressIndicatorIcon = createView<ProgressIndicatorIconProps>(
  ({size, status, animate, icon, inherits}) => {
    if (!isShowIcon(size, status)) {
      return null;
    }

    return (
      <Icon
        inherits={inherits}
        icon={icon || getIcon(size, status)}
        size={getIconSize(size, status)}
        css={[
          getIconFill(size, status),
          getIconBackgroundColor(size, status),
          getIconOffset(size, status),
          size === 'small' || size === 'medium'
            ? css({borderRadius: '50%'})
            : css({}),
          status === 'complete' && animate
            ? css({
                animation: `${iconInAnimation} 2s linear`,
              })
            : css({}),
        ]}
      />
    );
  },
  {
    css: {
      padding: 'space.0',
      position: 'relative',
      display: 'block',
    },
  },
);

/**
 * Normalize the status. If number and over 100 - complete. If small size and number status.
 *
 * @param status Progress indicator status.
 * @param size Progress indicator size.
 * @returns Normalized progress indicator status.
 */
function normalizeStatus(
  status: ProgressIndicatorStatus,
  size: ProgressIndicatorSize,
) {
  if (typeof status === 'number') {
    if (status >= 100) {
      return 'complete';
    } else if (size === 'small') {
      return 'in-progress';
    }
  }
  return status;
}

/**
 * Get the progress indicator status aria text.
 *
 * @param size Progress indicator size.
 * @param status Progress indicator status.
 * @returns The progress indicator status text as a string.
 */
function getAriaText(
  size: ProgressIndicatorSize,
  status: ProgressIndicatorStatus,
): string {
  const STATUS_LABELS = {
    'not-started': 'Not Started',
    'in-progress': 'In Progress',
    blocked: 'Blocked',
    complete: 'Complete',
  };

  if (typeof status === 'number') {
    switch (size) {
      case 'small':
        return 'In Progress';
      case 'large':
      case 'medium':
      default:
        return `${status}%`;
    }
  }

  return STATUS_LABELS[status];
}

/**
 * Get the progress indicator aria value.
 *
 * @param status Progress indicator status.
 * @returns The percent progress as a number if status is a number, or NaN otherwise.
 */
function getAriaValue(status: ProgressIndicatorStatus): number | undefined {
  if (typeof status === 'number') {
    return status;
  }

  return undefined;
}

export type ProgressIndicatorProps = View.IntrinsicElement<
  'div',
  {
    /**
     * `small`, `medium` (default), or `large`
     */
    size: ProgressIndicatorSize;

    /**
     * One of `not-started` (default), `in-progress`, `blocked`, `complete`, or an integer between 0 and 100 representing the percent that has been completed.
     */
    status: ProgressIndicatorStatus;

    /**
     * Icon to override the default. Only rendered in the `large` size.
     */
    icon?: IconAsset;

    /**
     * Should the progress indicator be animated?
     */
    animate?: boolean;

    subviews?: View.Subviews<{
      icon: typeof ProgressIndicatorIcon;
      bgCircle: typeof BackgroundCircle;
      donut: typeof ProgressIndicatorDonut;
    }>;
  }
>;

export const ProgressIndicatorConfig = createViewConfig({
  name: 'ProgressIndicator',
  props: {} as ProgressIndicatorProps,
  defaults: {
    size: 'medium',
    status: 'not-started',
  },
});

export const ProgressIndicator = ProgressIndicatorConfig.createView(
  ({size, status, icon, animate, subviews, ...props}) => {
    const indicatorForegroundColor = tokens['color.background.action.primary'];
    const indicatorBackgroundColor = tokens['color.hue.gray.100'];
    const successDonutBackgroundColor = tokens['color.hue.gray.0'];
    const normStatus = normalizeStatus(status, size);
    const prevStatus = React.useRef(normStatus);

    // The icon passed in by the user should only be used for the in-progress and not-started states (blocked and complete always use a consistent icon)
    const useCustomIcon =
      typeof normStatus === 'number' ||
      ['in-progress', 'not-started'].includes(normStatus);

    React.useEffect(() => {
      prevStatus.current = normStatus;
    }, [normStatus]);

    return (
      <view.div
        {...props}
        role="progressbar"
        aria-label={getAriaText(size, normStatus)}
        aria-valuenow={getAriaValue(normStatus)}
        aria-valuetext={getAriaText(size, normStatus)}
      >
        {/* Special case - if size=medium && status=not-started. */}
        <MediumNotStarted
          size={size}
          status={normStatus}
          foregroundColor={indicatorBackgroundColor}
        />

        {/* Background circle. */}
        <BackgroundCircle
          size={size}
          status={normStatus}
          inherits={subviews?.bgCircle}
        />

        {/* Donut progress indicator. */}
        <ProgressIndicatorDonut
          size={size}
          status={normStatus}
          inherits={subviews?.donut}
          prevStatus={prevStatus.current}
          foregroundColor={indicatorForegroundColor}
          backgroundColor={indicatorBackgroundColor}
          animate={!!animate}
        />

        {animate && normStatus === 'complete' ? (
          <CompletedAnimation
            size={size}
            foregroundColor={indicatorForegroundColor}
            backgroundColor={successDonutBackgroundColor}
          />
        ) : null}

        {/* Icon. */}
        <ProgressIndicatorIcon
          size={size}
          status={normStatus}
          animate={!!animate}
          inherits={subviews?.icon}
          icon={useCustomIcon ? icon : undefined}
        />
      </view.div>
    );
  },
  {
    css: {
      display: 'block',
      position: 'relative',
    },
    forward: {size: true},
    variants: {
      size: {
        small: getDonutSize('small'),
        medium: getDonutSize('medium'),
        large: getDonutSize('large'),
      },
    },
  },
);
